import { ComplianceCaseEventType } from "~/graphql/supergraphEnums";
import { type GetComplianceCaseAuditLogsQuery } from "~/graphql/supergraphTypes";

export type ComplianceCaseAuditLog =
  GetComplianceCaseAuditLogsQuery["getComplianceCaseAuditLogs"][number];

export const isExternalComment = (auditLog: ComplianceCaseAuditLog) =>
  auditLog.eventType === ComplianceCaseEventType.CommentAdded &&
  auditLog.eventData?.new?.internal === false;

export const getTextForEvent = (auditLog: ComplianceCaseAuditLog) => {
  const { eventType, eventData, createdBy } = auditLog;
  const newEventData = eventData.new;
  const oldEventData = eventData.old;
  const auditLogCreatedBy = createdBy?.name ?? "";
  switch (eventType) {
    case ComplianceCaseEventType.CaseCreated:
      return `Case created by ${newEventData?.created_by?.name ?? ""}`;
    case ComplianceCaseEventType.CaseUpdated:
      if (
        (oldEventData?.status === "ready" && newEventData?.status === "in_progress") ||
        (oldEventData?.status === "ready" && newEventData?.status === "pending_fund_review") ||
        (oldEventData?.status === "ready" &&
          newEventData?.status === "pending_data_resubmission") ||
        (oldEventData?.status === "ready" && newEventData?.status === "escalated") ||
        (oldEventData?.status === "incomplete" && newEventData?.status === "in_progress") ||
        (oldEventData?.status === "rejected" && newEventData?.status === "in_progress") ||
        (oldEventData?.status === "in_progress" && newEventData?.status === "in_progress") ||
        (oldEventData?.status === "escalated" && newEventData?.status === "escalated") ||
        (oldEventData?.status === "deferred" && newEventData?.status === "in_progress") ||
        (oldEventData?.status === "pending_data_submission" &&
          newEventData?.status === "in_progress")
      ) {
        return `Case assigned to ${auditLogCreatedBy}`;
      }
      if (
        newEventData?.status === "ready" &&
        (oldEventData?.status === "in_progress" ||
          oldEventData?.status === "pending_fund_review" ||
          oldEventData?.status === "pending_data_resubmission" ||
          oldEventData?.status === "escalated")
      ) {
        return `${auditLogCreatedBy} unassigned case from ${oldEventData.assignee?.name ?? ""}`;
      }
      if (oldEventData?.status === "in_progress" && newEventData?.status === "pending_review") {
        if (newEventData?.sub_status === "proposed_fund_escalation") {
          return `Case escalated and submitted for review by ${auditLogCreatedBy}`;
        } else {
          return `Submitted for review by ${auditLogCreatedBy}`;
        }
      }
      if (
        oldEventData?.status === "pending_review" &&
        newEventData?.status === "under_review"
      ) {
        return `Review assigned to ${auditLogCreatedBy}`;
      }
      if (
        oldEventData?.status === "under_review" &&
        newEventData?.status === "pending_review"
      ) {
        return `${createdBy?.name} unassigned review from ${oldEventData.checker?.name ?? ""}`;
      }
      if (oldEventData?.status === "under_review" && newEventData?.status === "ready") {
        return `${auditLogCreatedBy} unassigned review from ${oldEventData.checker?.name ?? ""}`;
      }
      if (oldEventData?.status === "under_review" && newEventData?.status === "incomplete") {
        return `Returned by ${auditLogCreatedBy} due to incomplete data`;
      }
      if (oldEventData?.status === "under_review" && newEventData?.status === "rejected") {
        return `Returned by ${auditLogCreatedBy} due to incorrect data`;
      }
      if (oldEventData?.status === "ready" && newEventData?.status === "closed") {
        return `Case closed by ${auditLogCreatedBy}`;
      }
      if (oldEventData?.status === "under_review" && newEventData?.status === "closed") {
        return `Approved by ${auditLogCreatedBy}`;
      }
      if (oldEventData?.status === "under_review" && newEventData?.status === "in_progress") {
        return `Returned by ${auditLogCreatedBy}`;
      }
      if (oldEventData?.status === "closed" && newEventData?.status === "ready") {
        return `Case reopened by ${auditLogCreatedBy}`;
      }
      if (oldEventData?.status === "under_review" && newEventData?.status === "escalated") {
        return `Escalation confirmed by ${auditLogCreatedBy}`;
      }
      if (oldEventData?.status === "escalated" && newEventData?.status === "pending_review") {
        return `${auditLogCreatedBy} progressed escalation status`;
      }
      if (oldEventData?.status === "under_review" && newEventData?.status === "deferred") {
        return `Case marked as deferred by ${auditLogCreatedBy}`;
      }
      if (
        oldEventData?.status === "pending_submission" &&
        newEventData?.status === "pending_data_submission"
      ) {
        return "Pending AML data/doc submission";
      }
      if (
        oldEventData?.status === "pending_data_submission" &&
        newEventData?.status === "ready"
      ) {
        return "AML data/documents provided";
      }
      if (oldEventData?.status === "pending_submission" && newEventData?.status === "ready") {
        return "Subscription submitted case ready for review";
      }
      if (
        oldEventData?.status === "pending_fund_review" &&
        newEventData?.status === "pending_review"
      ) {
        if (
          oldEventData?.sub_status === "escalated" &&
          (newEventData?.sub_status === "rejected" || newEventData?.sub_status === "accepted")
        ) {
          return `GP verdict entered by ${auditLogCreatedBy}`;
        }
      }
      if (
        oldEventData?.status === "under_review" &&
        (newEventData?.status === "pending_fund_review" ||
          newEventData?.status === "under_review")
      ) {
        if (
          (oldEventData?.sub_status === "proposed_fund_escalation" ||
            oldEventData?.sub_status === "proposed_escalation") &&
          newEventData?.sub_status === "escalated"
        ) {
          return `Case confirmed escalated by ${auditLogCreatedBy}`;
        }
      }
      if (
        oldEventData?.status === "pending_data_resubmission" &&
        newEventData?.status === "in_progress"
      ) {
        if (oldEventData?.sub_status === null && newEventData?.sub_status === "de_escalated") {
          return "GP escalation de-escalated";
        }
      }
      if (
        oldEventData?.status === "under_review" &&
        newEventData?.status === "pending_data_resubmission"
      ) {
        if (
          oldEventData?.sub_status === "proposed_deficient" &&
          newEventData?.sub_status === null
        ) {
          return "GP re-submission confirmed";
        }
      }
      // Subscription can be cancelled at any state, so not checking oldEventData.status
      if (newEventData?.status == "closed") {
        if (newEventData?.sub_status === "canceled") {
          return "Subscription canceled";
        }
      }
      if (newEventData?.status == "pending_submission") {
        if (
          newEventData?.sub_status === "reopened" ||
          newEventData?.sub_status == "restarted"
        ) {
          return "Subscription reopened by the GP";
        }
      }
      // New case state machine (BaseStateMachine) transitions
      if (oldEventData?.status === "not_ready" && newEventData?.status === "ready") {
        if (oldEventData?.sub_status === "pending_submission") {
          return "LP submission complete";
        }
      }
      if (
        oldEventData?.status === "not_ready" &&
        newEventData?.status === "not_ready" &&
        oldEventData?.sub_status === null &&
        newEventData?.sub_status === "pending_submission"
      ) {
        return "LP request in progress";
      }
      if (oldEventData?.status === "not_ready" && newEventData?.status === "closed") {
        if (newEventData?.sub_status === "approved") {
          return `Case closed by ${auditLogCreatedBy}`;
        }
        if (newEventData?.sub_status === "complete_no_change") {
          return "LP confirmed no changes required, case closed";
        }
      }
      return "";
    case ComplianceCaseEventType.CommentAdded:
      return newEventData?.internal === false
        ? `External comment to client by ${auditLogCreatedBy}`
        : `Internal comment by ${auditLogCreatedBy}`;
    case ComplianceCaseEventType.EmailSent: {
      const action = oldEventData ? "resent" : "sent";
      if (eventData?.object_name === "remediation_onboarding") {
        return `Remediation invitation ${action}`;
      }
      if (eventData?.object_name === "profile_refresh") {
        const emailAddresses = newEventData?.email_addresses;
        const emailList = Array.isArray(emailAddresses) ? emailAddresses.join(", ") : "";
        return emailList
          ? `Profile refresh invitation ${action} to ${emailList}`
          : `Profile refresh invitation ${action}`;
      }
      return "";
    }
    default:
      return "Unknown event";
  }
};
